import { useNavigate, useParams } from 'react-router-dom'

interface SideMenuProps {
  isOpen: boolean
  onClose: () => void
  onLogDrink: () => void
  onLogGap: () => void
  onLockScreen?: () => void
  onLogout?: () => void
  showUserActions?: boolean
}

export default function SideMenu({
  isOpen,
  onClose,
  onLogDrink,
  onLogGap,
  onLockScreen,
  onLogout,
  showUserActions = true,
}: SideMenuProps) {
  const navigate = useNavigate()
  const { userId } = useParams<{ userId: string }>()

  const menuItems = [
    ...(showUserActions
      ? [
          { icon: 'plus', label: 'Log Drink', onClick: onLogDrink, tooltip: 'Add a new drink entry' },
          { icon: 'gap', label: 'Log Gap', onClick: onLogGap, tooltip: 'Record a period you weren\'t tracking' },
        ]
      : []),
    ...(onLockScreen ? [{ icon: 'lock', label: 'Lock Screen', onClick: onLockScreen, tooltip: 'Lock the app until password is re-entered' }] : []),
    { icon: 'key', label: 'Key Recovery', onClick: () => navigate(userId ? `/u/${userId}/recover` : '/recover'), tooltip: 'Recover your passcode via email' },
    { icon: 'help', label: 'Need Help?', onClick: () => navigate(userId ? `/u/${userId}/help` : '/help'), tooltip: 'Crisis support and alcohol helplines', highlight: true },
  ]

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Side Panel */}
      <div
        className={`fixed top-0 left-0 h-full w-72 bg-[var(--bg-secondary)] z-50 transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between h-14 px-4 border-b border-[var(--border)]">
          <span className="text-[var(--text-primary)] font-bold">Menu</span>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[var(--bg-card)] transition-colors cursor-pointer"
            aria-label="Close menu"
          >
            <svg
              className="w-6 h-6 text-[var(--text-primary)]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Menu Items */}
        <div className="flex-1 py-2">
          {menuItems.map((item) => (
            <button
              key={item.label}
              onClick={() => {
                item.onClick()
                onClose()
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-card)] transition-colors text-left cursor-pointer"
              title={item.tooltip}
            >
              <span className="w-8 h-8 rounded-lg bg-[var(--bg-card)] flex items-center justify-center">
                {item.icon === 'plus' && (
                  <svg className="w-5 h-5 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                )}
                {item.icon === 'gap' && (
                  <svg className="w-5 h-5 text-[var(--accent-orange)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6v12M20 6v12M4 18h16" />
                  </svg>
                )}
                {item.icon === 'backup' && (
                  <svg className="w-5 h-5 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                )}
                {item.icon === 'lock' && (
                  <svg className="w-5 h-5 text-[var(--accent-orange)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                )}
                {item.icon === 'key' && (
                  <svg className="w-5 h-5 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                )}
                {item.icon === 'help' && (
                  <svg className="w-5 h-5 text-[var(--bac-danger)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                  </svg>
                )}
              </span>
              <span className={'highlight' in item && item.highlight ? 'text-[var(--bac-danger)] font-semibold' : 'text-[var(--text-primary)]'}>{item.label}</span>
            </button>
          ))}
        </div>

        {/* Feedback & Coffee */}
        <div className="border-t border-[var(--border)] py-2">
          <button
            onClick={() => { navigate(userId ? `/u/${userId}/feedback` : '/feedback'); onClose() }}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-card)] transition-colors text-left cursor-pointer"
            title="Send us feedback, bug reports, or feature requests"
          >
            <span className="w-8 h-8 rounded-lg bg-[var(--bg-card)] flex items-center justify-center">
              <svg className="w-5 h-5 text-[var(--text-secondary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
              </svg>
            </span>
            <span className="text-[var(--text-secondary)]">Any feedback?</span>
          </button>
          <a
            href="https://ko-fi.com/heigh"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-card)] transition-colors text-left"
          >
            <span className="w-8 h-8 rounded-lg bg-[var(--bg-card)] flex items-center justify-center text-lg">
              ☕
            </span>
            <span className="text-[var(--text-primary)]">Buy me a Coffee<span className="sr-only"> (opens in new tab)</span></span>
          </a>
        </div>

        {/* Logout - pinned to bottom */}
        {onLogout && (
          <div className="border-t border-[var(--border)] py-2">
            <button
              onClick={() => {
                onLogout()
                onClose()
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-card)] transition-colors text-left cursor-pointer"
            >
              <span className="w-8 h-8 rounded-lg bg-[var(--bg-card)] flex items-center justify-center">
                <svg className="w-5 h-5 text-[var(--bac-danger)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </span>
              <span className="text-[var(--text-primary)]">Log Out</span>
            </button>
          </div>
        )}
      </div>
    </>
  )
}
